import { useRef, useState } from "react";
import { ImagePlus, Loader2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { qgApi, errorMessage } from "@/lib/qgApi";
import { AuthImage } from "./AuthImage";

export function ImageField({ imageId, onChange, readOnly, testId }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image must be under 20 MB"); return; }
    setBusy(true);
    try {
      const { image_id, original_bytes, stored_bytes } = await qgApi.uploadImage(file);
      onChange(image_id);
      const kb = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
      toast.success(stored_bytes < original_bytes ? `Image saved (optimised ${kb(original_bytes)} → ${kb(stored_bytes)})` : "Image saved to quotation");
    } catch (err) {
      toast.error(errorMessage(err, "Image upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="relative h-24 w-full rounded-lg border border-dashed border-slate-300 dark:border-slate-600 overflow-hidden bg-slate-50 dark:bg-slate-900">
        {imageId ? <AuthImage imageId={imageId} className="h-full w-full" testId={`${testId}-img`} /> : (
          <div className="h-full flex items-center justify-center text-[11px] text-slate-400">No image</div>
        )}
        {busy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-brillien-600" /></div>}
      </div>
      {!readOnly && (
        <div className="flex gap-1">
          <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testId}-upload-btn`}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-medium rounded-md border border-slate-200 dark:border-slate-600 py-1 hover:border-brillien-400 hover:text-brillien-700 transition-colors">
            {imageId ? <RefreshCw className="h-3 w-3" /> : <ImagePlus className="h-3 w-3" />}{imageId ? "Replace" : "Upload"}
          </button>
          {imageId && (
            <button type="button" onClick={() => onChange(null)} data-testid={`${testId}-remove-btn`} title="Remove image"
              className="px-2 rounded-md border border-slate-200 dark:border-slate-600 text-slate-500 hover:text-red-600 transition-colors"><X className="h-3 w-3" /></button>
          )}
          <input ref={input} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-file-input`} />
        </div>
      )}
    </div>
  );
}
